import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 text-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">
            ← Back to App
          </Link>
        </div>

        <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>

        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-gray-300">
            <strong>Last Updated:</strong> November 22, 2025
          </p>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">1. Introduction</h2>
            <p className="text-gray-300">
              This Privacy Policy explains how backtime.live ("we," "us," or "our") collects, uses, and protects
              your information when you use our service timing application. We are committed to protecting
              your privacy and handling your data responsibly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">2. Information We Collect</h2>

            <h3 className="text-lg font-medium mt-4 mb-2">Account Information</h3>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li>Email address</li>
              <li>Display name</li>
              <li>Password (stored securely using industry-standard hashing)</li>
            </ul>

            <h3 className="text-lg font-medium mt-4 mb-2">Session Data</h3>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li>Session names and descriptions</li>
              <li>Schedule information (days and times)</li>
              <li>Phase and element configurations</li>
              <li>Timezone preferences</li>
            </ul>

            <h3 className="text-lg font-medium mt-4 mb-2">Usage Data</h3>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li>Access logs and timestamps</li>
              <li>Device and browser information</li>
              <li>IP addresses</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">3. How We Use Your Information</h2>
            <p className="text-gray-300">
              We use the collected information to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li>Provide and maintain the Service</li>
              <li>Authenticate your account and manage access</li>
              <li>Synchronize your sessions across devices</li>
              <li>Enable real-time collaboration features</li>
              <li>Improve and optimize the Service</li>
              <li>Send important service-related notifications</li>
              <li>Respond to support requests</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">4. Data Sharing and Disclosure</h2>
            <p className="text-gray-300">
              We do not sell your personal information. We may share data in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li><strong>Public Sessions:</strong> If you make a session public, anyone with the link or QR code can view the session details</li>
              <li><strong>Service Providers:</strong> We may share data with trusted third-party services that help us operate the Service (hosting, analytics)</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">5. Data Security</h2>
            <p className="text-gray-300">
              We implement appropriate security measures to protect your data:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security updates and monitoring</li>
              <li>Access controls and authentication</li>
            </ul>
            <p className="text-gray-300 mt-3">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee
              absolute security of your data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">6. Data Retention</h2>
            <p className="text-gray-300">
              We retain your data for as long as your account is active or as needed to provide the Service.
              If you delete your account, we will delete your personal data within 30 days, except where
              we are required to retain it for legal purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">7. Your Rights</h2>
            <p className="text-gray-300">
              You have the following rights regarding your data:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Export:</strong> Download your session data</li>
            </ul>
            <p className="text-gray-300 mt-3">
              To exercise these rights, please contact us at support@backtime.live.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">8. Cookies and Local Storage</h2>
            <p className="text-gray-300">
              We use local storage and session storage to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mt-2 space-y-1">
              <li>Keep you logged in</li>
              <li>Store your preferences</li>
              <li>Cache session data for performance</li>
            </ul>
            <p className="text-gray-300 mt-3">
              You can clear this data through your browser settings, but this may affect functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">9. Third-Party Services</h2>
            <p className="text-gray-300">
              The Service may integrate with third-party services (such as Google Sign-In). These services
              have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">10. Children's Privacy</h2>
            <p className="text-gray-300">
              The Service is not intended for children under 13 years of age. We do not knowingly collect
              personal information from children under 13. If you believe we have collected such information,
              please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">11. International Data Transfers</h2>
            <p className="text-gray-300">
              Your data may be processed and stored in countries other than your own. By using the Service,
              you consent to such transfers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">12. Changes to This Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of significant changes
              by posting a notice on the Service or sending you an email. Your continued use of the Service
              after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-6 mb-3">13. Contact Us</h2>
            <p className="text-gray-300">
              If you have questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-gray-300 mt-2">
              Email: support@backtime.live
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-gray-700 text-center text-sm text-gray-500">
          <Link to="/terms" className="hover:text-gray-300 mr-4">Terms of Service</Link>
          <Link to="/about" className="hover:text-gray-300">About</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
